import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FiDownload, FiSearch, FiX } from "react-icons/fi";
import SkeletonBase from "newui/components/Skeleton";
import FallbackImage from "components/FallbackImage/FallbackImage";
import { FullDrawContainer } from "./FullDrawContainer";
import { useCompanyKeyPersonnel } from "../hooks/useCompanyKeyPersonnel";
import type { KeyPersonnel } from "../types/supplier-search.types";
import { PROFILE_TYPE_CONSULTANT } from "utils/constants";

interface KeyPersonnelFullDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	companyId: number;
}

const getTableHeaders = (showConsultantFields: boolean) => {
	const headers = [
		{ label: "", width: "w-[60px]" },
		{ label: "Name", width: "w-[260px]" },
		{ label: "Position", width: "w-[220px]" },
		{ label: "Industry Experience", width: "w-[150px]" },
		{ label: "Top 3 Sectors", width: "flex-1 min-w-[260px]" },
	];

	if (showConsultantFields) {
		headers.push({ label: "Key Discipline", width: "w-[220px]" });
	}

	headers.push({ label: "Resume", width: "w-[100px]" })

	return headers;
};

const PersonnelRow: React.FC<{
	person: KeyPersonnel;
	showConsultantFields: boolean;
}> = ({ person, showConsultantFields }) => {
	const initials = `${person.first_name?.[0] ?? ""}${person.last_name?.[0] ?? ""}`.toUpperCase();
	const resume = person.resume && person.resume.length > 0 ? person.resume.find((resume) => !!resume.is_default)?.file_path : null;

	return (
		<div className="flex items-center border-b border-gray-100 px-4 py-3 hover:bg-gray-50 even:bg-blue-50">
			<div className="w-[60px] flex-shrink-0 pr-3">
				{person.avatar_location ? (
					<FallbackImage
						src={person.avatar_location}
						alt={`${person.first_name} ${person.last_name}`}
						className="h-[50px] w-[50px] rounded-full object-cover"
					/>
				) : (
					<div className="flex h-[50px] w-[50px] items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700">
						{initials}
					</div>
				)}
			</div>

			<div className="w-[260px] flex-shrink-0 pr-3">
				<p className="text-sm font-medium text-gray-900">
					{person.first_name} {person.last_name}
				</p>
			</div>

			<div className="w-[220px] flex-shrink-0 pr-3">
				<p className="text-sm text-gray-600">
					{person.position || "—"}
				</p>
			</div>

			<div className="w-[150px] flex-shrink-0 pr-3">
				<p className="text-sm text-gray-600">
					{person.experience !== null ? `${person.experience} Years` : "—"}
				</p>
			</div>

			<div className="flex-1 min-w-[260px] pr-3">
				{person.sectors && person.sectors.length > 0 ? (
					<div className="flex flex-col gap-0.5">
						{person.sectors.slice(0, 3).map((sector) => (
							<p key={sector.id} className="text-sm text-gray-600">
								{sector.name}
							</p>
						))}
					</div>
				) : (
					<p className="text-sm text-gray-400">—</p>
				)}
			</div>

			{showConsultantFields && (
				<div className="w-[200px] flex-shrink-0">
					{person.disciplines && person.disciplines.length > 0 ? (
						<div className="flex flex-col gap-0.5">
							{person.disciplines.map((discipline) => (
								<p key={discipline.id} className="text-sm text-gray-600">
									{discipline.name}
								</p>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-400">—</p>
					)}
				</div>
			)}

			<div className="flex-shrink-0 min-w-[100px] pr-3">
				{resume ? <a target='_blank' href={resume}><div className="flex flex-row gap-2"><FiDownload className="h-4 w-4" /> Resume</div></a> : 'N/A'}
			</div>
		</div>
	);
};

export const KeyPersonnelFullDrawer: React.FC<KeyPersonnelFullDrawerProps> = ({
	isOpen,
	onClose,
	companyId,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const { data: personnel, isLoading } = useCompanyKeyPersonnel(
		isOpen ? companyId : null
	);

	const profileTypeId = useSelector(
		(state: any) => state.search?.activeCompany?.profile_type?.id
	);

	const showConsultantFields = profileTypeId === PROFILE_TYPE_CONSULTANT;
	const tableHeaders = getTableHeaders(showConsultantFields);

	const filteredPersonnel = useMemo(() => {
		if (!personnel) {
			return [];
		}
		if (!searchTerm.trim()) {
			return personnel;
		}
		const term = searchTerm.toLowerCase();
		return personnel.filter((person) => {
			const fullName =
				`${person.first_name} ${person.last_name}`.toLowerCase();
			const position = (person.position ?? "").toLowerCase();
			const sectorNames = (person.sectors ?? [])
				.map((s) => s.name.toLowerCase())
				.join(" ");
			const disciplineNames = (person.disciplines ?? [])
				.map((d) => d.name.toLowerCase())
				.join(" ");
			return (
				fullName.includes(term) ||
				position.includes(term) ||
				sectorNames.includes(term) ||
				disciplineNames.includes(term)
			);
		});
	}, [personnel, searchTerm]);

	const handleClearSearch = () => {
		setSearchTerm("");
	};

	return (
		<FullDrawContainer
			isOpen={isOpen}
			onClose={onClose}
			title="View all personnel"
		>
			{/* Search */}
			<div className="mb-4">
				<div className="relative">
					<FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						placeholder="Search by name, position, sector or discipline..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
					/>
					{searchTerm && (
						<button
							type="button"
							onClick={handleClearSearch}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
						>
							<FiX className="h-4 w-4" />
						</button>
					)}
				</div>
			</div>

			{/* Table header */}
			<div className="flex items-center border-b border-gray-200 px-4 py-2">
				{tableHeaders.map((header) => (
					<div
						key={header.label || "avatar"}
						className={`${header.width} flex-shrink-0`}
					>
						{header.label && (
							<p className="text-xs font-medium uppercase tracking-wide text-gray-500">
								{header.label}
							</p>
						)}
					</div>
				))}
			</div>

			{/* Loading */}
			{isLoading && (
				<div className="flex flex-col">
					{[...Array(5)].map((_, i) => (
						<div
							key={i}
							className="flex items-center border-b border-gray-100 px-4 py-3"
						>
							<div className="w-[60px] flex-shrink-0 pr-3">
								<SkeletonBase width="50px" height="50px" />
							</div>
							<div className="min-w-[160px] flex-1 pr-3">
								<SkeletonBase width="70%" height="20px" />
							</div>
							<div className="w-[160px] flex-shrink-0 pr-3">
								<SkeletonBase width="60%" height="20px" />
							</div>
							<div className="w-[140px] flex-shrink-0 pr-3">
								<SkeletonBase width="50%" height="20px" />
							</div>
							<div className="w-[220px] flex-shrink-0 pr-3">
								<SkeletonBase width="80%" height="20px" />
							</div>
							{showConsultantFields && (
								<div className="w-[200px] flex-shrink-0">
									<SkeletonBase width="70%" height="20px" />
								</div>
							)}
						</div>
					))}
				</div>
			)}

			{/* Empty state */}
			{!isLoading && filteredPersonnel.length === 0 && (
				<div className="flex flex-col items-center justify-center py-12">
					<p className="text-sm text-gray-500">
						{searchTerm
							? "No personnel matching your search"
							: "No key personnel found for this company"}
					</p>
				</div>
			)}

			{/* Personnel list */}
			{!isLoading && filteredPersonnel.length > 0 && (
				<div className="flex flex-col">
					{filteredPersonnel.map((person) => (
						<PersonnelRow
							key={person.id}
							person={person}
							showConsultantFields={showConsultantFields}
						/>
					))}
				</div>
			)}
		</FullDrawContainer>
	);
};
